import { z } from 'zod';
import { createTool } from '../_registry.js';
import type { NetworkEvent, FennecSession } from '../../session/types.js';
import type { Route } from '../../browser/types.js';

// ─── Runtime metadata ───────────────────────────────────────────
// Extends FennecSession.metadata with fields used at runtime.
// Cast once through a narrow interface instead of scattering `as any`.

interface InterceptorRef {
  urlPattern: string;
  isMock?: boolean;
}

interface SessionRuntimeMeta {
  interceptorRefs: Record<string, InterceptorRef>;
}

function getRuntimeMeta(session: FennecSession): SessionRuntimeMeta {
  // Initialise the refs map on first access so callers can write without
  // null-checking. This is safe because JavaScript object identity is preserved.
  const m = session.metadata as SessionRuntimeMeta;
  if (!m.interceptorRefs) m.interceptorRefs = {};
  return m;
}

export const networkGetLogs = createTool({
  name: 'network_get_logs',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> 🌐 Get all network request logs from the page. Filter by HTTP status code, method (GET/POST/etc), or URL pattern. Returns requests[], failedCount (>=400), slowCount (>1s). Use when you need to inspect API calls, check request/response patterns, or monitor network activity. More comprehensive than diagnose_network which only shows failures.`',
  inputSchema: z.object({
    status: z.number().optional().describe('Filter by HTTP status code'),
    method: z.string().optional().describe('Filter by HTTP method (GET, POST, etc.)'),
    urlPattern: z.string().optional().describe('Filter by URL pattern'),
    since: z
      .string()
      .optional()
      .describe('ISO timestamp filter — only return requests after this time'),
    limit: z.number().optional().default(50).describe('Maximum number of requests to return'),
    includeResponseBodies: z
      .boolean()
      .optional()
      .default(false)
      .describe(
        'Include response bodies in the log. OFF by default to keep output lean; bodies are still available via network_get_request_detail.',
      ),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    let requests = session.networkBuffer;

    if (input.since) {
      const since = new Date(input.since).getTime();
      if (!isNaN(since)) {
        requests = requests.filter((r) => new Date(r.timestamp).getTime() >= since);
      }
    }
    if (input.status) {
      const status = input.status;
      requests = requests.filter((r) => r.status === status);
    }
    if (input.method) {
      const method = input.method.toUpperCase();
      requests = requests.filter((r) => r.method.toUpperCase() === method);
    }
    if (input.urlPattern) {
      const pattern = input.urlPattern;
      requests = requests.filter((r) => r.url.includes(pattern));
    }

    if (input.limit && input.limit > 0) {
      requests = requests.slice(-input.limit);
    }

    // By default response bodies are omitted from the log (they can be
    // large); surface them only when explicitly requested.
    if (!input.includeResponseBodies) {
      requests = requests.map(({ responseBody, ...rest }) => rest);
    }

    const failedCount = requests.filter((r) => r.status >= 400).length;
    const slowCount = requests.filter((r) => r.duration > 1000).length;

    return responseBuilder.success(
      {
        // requestId is already part of each event; callers can pass it
        // straight to network_get_request_detail (avoids substring-URL
        // drift / the "Request not found in buffer" race).
        requests,
        count: requests.length,
        failedCount,
        slowCount,
      },
      sessionManager.buildMeta(session),
    );
  },
});

export const networkGetFailedRequests = createTool({
  name: 'network_get_failed_requests',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> ❌ Get ONLY failed network requests (HTTP status >= 400). Returns requests[] with their URLs, methods, and status codes. Faster than network_get_logs when you only care about failures. Use for quick error checking — like after form submission or API call.`',
  inputSchema: z.object({
    since: z.string().optional().describe('ISO timestamp filter'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    const requests = session.networkBuffer.filter((r) => r.status >= 400);

    return responseBuilder.success(
      {
        requests,
        count: requests.length,
      },
      sessionManager.buildMeta(session),
    );
  },
});

export const networkGetCorsIssues = createTool({
  name: 'network_get_cors_issues',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> 🔒 Detect REAL CORS-related issues from network logs (preflight OPTIONS noise excluded by default). Returns issues[] with affected URLs, methods, and reason. Use when you see blocked requests in console or cross-origin errors. Genuine CORS failures show as status=0 on a non-preflight request.`',
  inputSchema: z.object({
    excludePreflight: z
      .boolean()
      .optional()
      .default(true)
      .describe(
        "Exclude OPTIONS preflight requests from results (they're normal noise, not errors)",
      ),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);

    const issues: Array<{ url: string; method: string; reason: string }> = [];

    for (const req of session.networkBuffer) {
      // OPTIONS preflight is normal browser behavior, not a failure.
      if (input.excludePreflight && req.method.toUpperCase() === 'OPTIONS') continue;

      if (req.status === 0) {
        issues.push({
          url: req.url,
          method: req.method,
          reason: 'Request blocked (status 0) - likely CORS or mixed content',
        });
      }
    }

    return responseBuilder.success(
      {
        issues,
        count: issues.length,
      },
      sessionManager.buildMeta(session),
    );
  },
});

export const networkClearLogs = createTool({
  name: 'network_clear_logs',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> 🧹 Clear the network request log buffer. Returns cleared=true. Use before performing an action (like clicking a button or submitting a form) so you can see only the new network requests that result from that action.`',
  inputSchema: z.object({
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    session.networkBuffer = [];
    return responseBuilder.success({ cleared: true }, sessionManager.buildMeta(session));
  },
});

export const networkWaitForRequest = createTool({
  name: 'network_wait_for_request',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> ⏳ Wait for a specific network request to happen. Provide a URL pattern (glob or substring) and optional HTTP method. Returns full request/response details including headers, status, postData. Use when you need to capture API responses, intercept form submissions, or verify that a specific request was made. Has timeout (default 30s).`',
  inputSchema: z.object({
    urlPattern: z.string().describe('URL or pattern to wait for (glob or substring)'),
    method: z.string().optional().describe('HTTP method filter (GET, POST, etc.)'),
    timeout: z.number().optional().default(30000).describe('Timeout in milliseconds'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    const startTime = Date.now();
    try {
      // Use Playwright's waitForRequest for reliable waiting
      const request = await session.browser.waitForRequest(
        (req) => {
          const urlMatch = req.url().includes(input.urlPattern);
          const methodMatch = input.method
            ? req.method().toUpperCase() === input.method.toUpperCase()
            : true;
          return urlMatch && methodMatch;
        },
        { timeout: input.timeout },
      );

      const response = await request.response();
      return responseBuilder.success(
        {
          request: {
            url: request.url,
            method: request.method,
            headers: request.headers,
            postData: request.postData,
            resourceType: request.resourceType,
          },
          response: response
            ? {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                url: response.url,
              }
            : null,
          elapsed: Date.now() - startTime,
        },
        sessionManager.buildMeta(session),
      );
    } catch (error) {
      return responseBuilder.error(error, {
        code: 'REQUEST_TIMEOUT',
        suggestions: [
          `No request matching "${input.urlPattern}" received within ${input.timeout}ms`,
          'Try broadening the URL pattern',
          'Check if the page is making the expected request',
        ],
      });
    }
  },
});

export const networkWaitForApiResponse = createTool({
  name: 'network_wait_for_api_response',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> ⏳ Block until an API RESPONSE comes back (not just the request leaving). Provide a URL pattern and optional HTTP method/status filter. Polls the network buffer and returns the full response: status, headers, body, duration, and size. Use after clicking a button or submitting a form when you need the API result before continuing — unlike network_wait_for_request, this guarantees the response has arrived. Has timeout (default 30s).`',
  inputSchema: z.object({
    urlPattern: z.string().describe('URL or pattern to wait for (substring match)'),
    method: z.string().optional().describe('HTTP method filter (GET, POST, etc.)'),
    statusMin: z
      .number()
      .optional()
      .default(200)
      .describe(
        'Minimum acceptable status code (default 200). Set 0 to match even failed/blocked responses.',
      ),
    timeout: z.number().optional().default(30000).describe('Timeout in milliseconds'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    const start = Date.now();
    const deadline = start + (input.timeout ?? 30000);
    const method = input.method?.toUpperCase();

    try {
      let match: (typeof session.networkBuffer)[number] | undefined;
      while (Date.now() < deadline) {
        match = session.networkBuffer.find((r) => {
          const urlOk = r.url.includes(input.urlPattern);
          const methodOk = method ? r.method.toUpperCase() === method : true;
          // status 0 = no response yet (pending/blocked); wait until a real status arrives
          const statusOk = r.status >= (input.statusMin ?? 0);
          return urlOk && methodOk && statusOk;
        });
        if (match) break;
        await new Promise((res) => setTimeout(res, 100));
      }

      if (!match) {
        return responseBuilder.error(
          new Error(`No API response matching "${input.urlPattern}" within ${input.timeout}ms`),
          {
            code: 'RESPONSE_TIMEOUT',
            suggestions: [
              'Try broadening the URL pattern',
              'Check if the expected API call was actually made',
              'Use network_get_logs to inspect what requests happened',
            ],
          },
        );
      }

      const body = match.responseBody;
      let parsed: unknown = undefined;
      if (body) {
        try {
          parsed = JSON.parse(body);
        } catch {
          parsed = body;
        }
      }

      return responseBuilder.success(
        {
          url: match.url,
          method: match.method,
          status: match.status,
          statusText: match.statusText,
          duration: match.duration,
          responseHeaders: match.responseHeaders,
          responseBody: parsed,
          responseBodyRaw: body,
          responseSize: body ? Buffer.byteLength(body) : 0,
          elapsed: Date.now() - start,
        },
        sessionManager.buildMeta(session),
      );
    } catch (error) {
      return responseBuilder.error(error, { code: 'RESPONSE_WAIT_ERROR' });
    }
  },
});

export const networkGetRequestDetail = createTool({
  name: 'network_get_request_detail',
  category: 'devtools',
  description:
    '`<use_case>Network inspector</use_case> 🔎 Get FULL details of a network request including request/response headers, body, timing, and size. Provide either exact URL or requestId (from network_get_logs). More detailed than network_get_logs which only shows summary. Use when you need to inspect response bodies, request payloads, or timing breakdown.`',
  inputSchema: z
    .object({
      url: z.string().optional().describe('URL of the request to get detail for'),
      requestId: z.string().optional().describe('Request ID from the buffer'),
      fullBody: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          'Return the FULL response body. By default large bodies are truncated to keep the result readable.',
        ),
      sessionId: z.string().optional().describe('Session ID'),
    })
    .refine((data) => data.url || data.requestId, {
      message: 'Either url or requestId must be provided',
    }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    let request: (typeof session.networkBuffer)[0] | undefined;

    if (input.requestId) {
      request = session.networkBuffer.find((r) => r.requestId === input.requestId);
    } else if (input.url) {
      // Try an exact match first, then fall back to substring so a copied
      // (possibly truncated) URL from network_get_logs still resolves.
      request =
        session.networkBuffer.find((r) => r.url === input.url) ??
        session.networkBuffer.find((r) => r.url.includes(input.url!)) ??
        session.networkBuffer.find((r) => input.url!.includes(r.url));
    }

    if (!request) {
      return responseBuilder.error(new Error('Request not found in buffer'), {
        code: 'ELEMENT_NOT_FOUND',
        suggestions: [
          'Use network_get_logs to see available requests and copy their requestId',
          'Verify the URL or requestId',
          'The buffer may have been cleared or the request evicted (max 1000 events) — call network_get_logs again',
        ],
      });
    }

    const MAX_RESPONSE_BODY = 4000;
    const rawBody = request.responseBody;
    const truncated = !!rawBody && !input.fullBody && rawBody.length > MAX_RESPONSE_BODY;
    const responseBody = truncated
      ? `${rawBody!.slice(0, MAX_RESPONSE_BODY)}\n…[truncated ${rawBody!.length - MAX_RESPONSE_BODY} chars — pass fullBody:true for the complete body]`
      : rawBody;

    return responseBuilder.success(
      {
        request: {
          url: request.url,
          method: request.method,
          status: request.status,
          duration: request.duration,
          requestHeaders: request.requestHeaders,
          responseHeaders: request.responseHeaders,
          requestBody: request.requestBody,
          responseBody,
          responseBodyTruncated: truncated,
          timestamp: request.timestamp,
        },
      },
      sessionManager.buildMeta(session),
    );
  },
});

// ─── Network Intercept / Mock ────────────────────────────────────

export const networkIntercept = createTool({
  name: 'network_intercept',
  category: 'devtools',
  description:
    '`<use_case>Network mocking</use_case> 🚦 Intercept network requests matching a URL pattern. Returns an interceptorId for later removal. Use when you want to log, modify, or block specific requests. Unlike network_mock_response which replaces responses, this just intercepts and allows the request to continue. Combine with network_remove_intercept to stop intercepting.`',
  inputSchema: z.object({
    urlPattern: z.string().describe('URL pattern to intercept (glob or substring)'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    try {
      const interceptorId = `int_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const handler = async (route: Route) => {
        await route.continue();
      };
      await session.browser.route(input.urlPattern, handler);
      getRuntimeMeta(session).interceptorRefs[interceptorId] = {
        urlPattern: input.urlPattern,
      };
      return responseBuilder.success(
        { interceptorId, active: true },
        sessionManager.buildMeta(session),
      );
    } catch (error) {
      return responseBuilder.error(error, {
        code: 'NETWORK_INTERCEPT_FAILED',
        suggestions: ['Check if the page is still open', 'Verify URL pattern syntax'],
      });
    }
  },
});

export const networkRemoveIntercept = createTool({
  name: 'network_remove_intercept',
  category: 'devtools',
  description:
    "`<use_case>Network mocking</use_case> 🗑️ Remove a network intercept or mock that was previously set by network_intercept or network_mock_response. Provide the interceptorId or mockId. Returns removed=true/false. Use after you're done intercepting to restore normal network behavior.`",
  inputSchema: z.object({
    interceptorId: z.string().describe('Interceptor ID to remove'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    try {
      const refs = getRuntimeMeta(session).interceptorRefs;
      const intercept = refs[input.interceptorId];
      if (!intercept) {
        return responseBuilder.success(
          { removed: false, reason: 'Interceptor not found' },
          sessionManager.buildMeta(session),
        );
      }
      await session.browser.unroute(intercept.urlPattern);
      delete refs[input.interceptorId];
      return responseBuilder.success({ removed: true }, sessionManager.buildMeta(session));
    } catch (error) {
      return responseBuilder.error(error, {
        code: 'NETWORK_INTERCEPT_FAILED',
      });
    }
  },
});

export const networkMockResponse = createTool({
  name: 'network_mock_response',
  category: 'devtools',
  description:
    '`<use_case>Network mocking</use_case> 🎭 Mock a response for a URL pattern — intercept matching requests and fulfill them with a custom status, body, and headers. Returns a mockId for later removal. Use for testing error states, simulating API responses, or testing without a real backend. More powerful than network_intercept because it replaces the response entirely.`',
  inputSchema: z.object({
    urlPattern: z.string().describe('URL pattern to mock (glob or substring)'),
    statusCode: z.number().optional().default(200).describe('HTTP status code'),
    body: z.string().optional().default('').describe('Response body'),
    contentType: z.string().optional().default('application/json').describe('Content-Type header'),
    headers: z.record(z.string(), z.string()).optional().describe('Additional response headers'),
    sessionId: z.string().optional().describe('Session ID'),
  }),
  handler: async (input, { sessionManager, responseBuilder }) => {
    const session = sessionManager.getOrDefault(input.sessionId);
    try {
      const mockId = `mock_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await session.browser.route(input.urlPattern, async (route) => {
        await route.fulfill({
          status: input.statusCode,
          contentType: input.contentType,
          body: input.body,
          headers: input.headers,
        });
      });
      const refs = getRuntimeMeta(session).interceptorRefs;
      refs[mockId] = {
        urlPattern: input.urlPattern,
        isMock: true,
      };
      return responseBuilder.success({ mockId, active: true }, sessionManager.buildMeta(session));
    } catch (error) {
      return responseBuilder.error(error, {
        code: 'NETWORK_INTERCEPT_FAILED',
        suggestions: ['Check if the page is still open', 'Verify URL pattern syntax'],
      });
    }
  },
});

export const networkApiCall = createTool({
  name: 'network_api_call',
  category: 'devtools',
  description:
    '`<use_case>API Client</use_case> 🌐 Make an ad-hoc HTTP request to any API endpoint. Supports JSON payloads, custom headers, and methods (GET, POST, PUT, DELETE, PATCH). Returns response status, headers, and body (auto-parses JSON). Use when you need to inspect backend API responses, verify API functionality, or check schema/endpoints directly without using the browser UI or spawning curl in shell.`',
  inputSchema: z.object({
    url: z.string().url().describe('The URL to make the request to'),
    method: z
      .enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'])
      .optional()
      .default('GET')
      .describe('HTTP method'),
    headers: z.record(z.string(), z.string()).optional().describe('Optional request headers'),
    body: z.string().optional().describe('Optional request body (stringified JSON or text)'),
    timeout: z.number().optional().default(10000).describe('Timeout in milliseconds'),
  }),
  handler: async (input, { responseBuilder }) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), input.timeout ?? 10000);
    try {
      const response = await fetch(input.url, {
        method: input.method ?? 'GET',
        headers: input.headers,
        body: ['GET', 'HEAD'].includes(input.method!) ? undefined : input.body,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();
      let body: string | Record<string, unknown> = text;
      if (contentType.includes('application/json')) {
        try {
          body = JSON.parse(text) as Record<string, unknown>;
        } catch {
          // Keep as string if parsing fails
        }
      }

      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });

      return responseBuilder.success({
        status: response.status,
        statusText: response.statusText,
        headers,
        body,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      return responseBuilder.error(err, { code: 'API_CALL_FAILED' });
    }
  },
});
